import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import PriceSummary from '@apps/card/components/PriceSummary';
import { StationeryPrintOrderDetailsFragment } from '@graphql/generated';
import ReceiptBlock from '@apps/card/components/Blocks/ReceiptBlock';
import { templateToTitle } from '@apps/card/utils';
import { ThemeJson } from '@apps/card/routes/CardCustomizer';
import useEnvelopePreview from '@apps/card/hooks/useEnvelopePreview';
import { BLANK_ENVELOPE_FOREGROUND, DraftPreview } from '@apps/card/components/DraftPreview';
import { useCardCustomizerConfig } from '@apps/card/hooks/useCardCustomizedConfig';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useTranslation } from '@shared/core';

// const Block = ({ children, title }: { title: string; children: React.ReactNode }) => {
//   return (
//     <Flex flexDirection="column" rowGap={5}>
//       <TextV2 typographyVariant="hed1">{title}</TextV2>
//       {children}
//     </Flex>
//   );
// };

/**
 * TODO:
 *  - Need some way to get payment method
 */
export const PaymentSummary = ({ order }: { order: StationeryPrintOrderDetailsFragment }) => {
  const { tq } = useTranslation('stationery');
  const { helpers } = useCardCustomizerConfig(order.cardDraft!.format, order.cardDraft!.stationeryTemplate.category);
  const envelopePreview = useEnvelopePreview({ order });
  const getCategoryName = useStationeryTemplateCategoryTranslations('stationeryTemplateCategoryToTile');
  const categoryName = getCategoryName(order.cardDraft!.stationeryTemplate.category);

  const cardJSON = order.cardDraftJSONBlob as ThemeJson;
  const { taxInMinorUnits, discount, cardDraft } = order;

  return (
    <Flex
      width={'100%'}
      maxWidth="100%"
      alignSelf="center"
      paddingX={{ _: 6, md: 0 }}
      flexDirection={{ _: 'column', md: 'row' }}
      rowGap={10}
      columnGap={140}
    >
      <Flex
        display={{ _: 'none', md: 'flex' }}
        flexDirection="column"
        flex={1}
        gap={10}
        maxWidth="400px"
      >
        {order.printCardBackPreviewUrl && (
          <Flex
            flexDirection="column"
            gap={5}
            alignItems="center"
          >
            <TextV2
              typographyVariant="label2"
              color="mono8"
            >
              {tq`Card Back`}
            </TextV2>
            <DraftPreview
              images={{ cardBack: order.printCardBackPreviewUrl }}
              diecut={cardJSON.customizations.shape}
            />
          </Flex>
        )}
        {envelopePreview.isPreviewAvailable && (
          <>
            <Flex
              flexDirection="column"
              gap={5}
              alignItems="center"
            >
              <TextV2
                typographyVariant="label2"
                color="mono8"
              >
                {tq`Envelope Front`}
              </TextV2>
              <DraftPreview images={{ envelopFront: envelopePreview.frontImageUrl ?? BLANK_ENVELOPE_FOREGROUND }} />
            </Flex>
            <Flex
              flexDirection="column"
              gap={5}
              alignItems="center"
            >
              <TextV2
                typographyVariant="label2"
                color="mono8"
              >
                {tq`Envelope Back`}
              </TextV2>
              <DraftPreview images={{ envelopeBack: envelopePreview.backImageUrl ?? BLANK_ENVELOPE_FOREGROUND }} />
            </Flex>
          </>
        )}
      </Flex>
      <Flex
        flexDirection="column"
        flex={1}
      >
        <PriceSummary
          promotion={discount}
          salesTax={taxInMinorUnits}
          templateCategory={cardDraft!.stationeryTemplate.category}
          cardConfig={cardJSON}
          attributes={order.selectedProduct?.attributes ?? []}
          priceUpTo="total"
          flex={1}
          maxWidth={{ md: '440px' }}
          width="100%"
          alignSelf="flex-end"
        >
          {summary => (
            <>
              <TextV2 typographyVariant="hed3">{tq`Order Summary`}</TextV2>
              <ReceiptBlock
                title={categoryName}
                subTitle={templateToTitle(order.cardDraft!.stationeryTemplate)}
                lines={summary.card}
              />
              {!!summary.back.length && (
                <ReceiptBlock
                  title={tq`Back`}
                  lines={summary.back}
                />
              )}
              {helpers.isStepEnabled('envelope') && (
                <ReceiptBlock
                  title={tq`Envelope`}
                  lines={summary.envelope}
                />
              )}
              <ReceiptBlock
                title={tq`Shipping Method`}
                lines={summary.shipping}
              />
              {!!taxInMinorUnits && (
                <ReceiptBlock
                  title={tq`Taxes`}
                  lines={summary.tax}
                />
              )}
            </>
          )}
        </PriceSummary>
      </Flex>
    </Flex>
  );
};
